import React from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { GlassButton } from '../ui/GlassButton';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { formatCurrency, formatK } from '../charts/d3/format';
import { useLoanPlannerSummary } from '../../hooks/useLoanPlannerSummary';
import { formatMonthKeyLabel } from '../../utils/loanAmortization';

type HomeLoanCardProps = {
  onPress?: () => void;
  actionLabel?: string;
  emptyActionLabel?: string;
};

function formatRate(rate: number): string {
  return `${Math.round(rate * 1000) / 1000}%`;
}

export function HomeLoanCard({
  onPress,
  actionLabel = 'See all',
  emptyActionLabel = 'Add',
}: HomeLoanCardProps) {
  const summary = useLoanPlannerSummary();

  if (!summary.hydrated) {
    return (
      <GlassCard style={styles.card}>
        <View style={styles.loadingRow}>
          <ActivityIndicator color={colors.accent} />
          <Text style={styles.hint}>Loading loan…</Text>
        </View>
      </GlassCard>
    );
  }

  if (!summary.hasData) {
    return (
      <GlassCard style={styles.card}>
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={styles.title}>Loan planner</Text>
            <Text style={styles.hint}>Set amount, rate, and term</Text>
          </View>
          <View style={styles.headerActions}>
            <RefreshButton compact />
            {onPress && (
              <GlassButton
                variant="primary"
                size="sm"
                onPress={onPress}
                accessibilityLabel="Add loan plan"
              >
                {emptyActionLabel}
              </GlassButton>
            )}
          </View>
        </View>
        <Text style={styles.emptyCopy}>
          We’ll calculate your monthly payment and show interest vs principal — synced on
          mobile and web.
        </Text>
      </GlassCard>
    );
  }

  const {
    label,
    principal,
    interestRatePct,
    termYears,
    monthlyPayment,
    monthlyInterest,
    monthlyPrincipal,
    interestSharePct,
    principalSharePct,
    hasExtras,
    startMonth,
  } = summary;

  return (
    <GlassCard style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>{label}</Text>
          <Text style={styles.hint}>
            {startMonth
              ? `Started ${formatMonthKeyLabel(startMonth)}${
                  hasExtras ? ' · extras applied' : ''
                }`
              : hasExtras
                ? 'Extras applied · synced across devices'
                : 'Synced across devices'}
          </Text>
        </View>
        <View style={styles.headerActions}>
          <RefreshButton compact />
          {onPress && (
            <GlassButton
              variant="ghost"
              size="sm"
              onPress={onPress}
              accessibilityLabel={`${actionLabel} loan`}
            >
              {actionLabel}
            </GlassButton>
          )}
        </View>
      </View>

      <View style={styles.hero}>
        <Text style={styles.heroLabel}>Monthly payment</Text>
        <Text style={styles.heroValue}>{formatCurrency(monthlyPayment)}</Text>
      </View>

      <View style={styles.kpiRow}>
        <View style={styles.kpi}>
          <Text style={styles.kpiLabel}>Amount</Text>
          <Text style={styles.kpiValue}>{formatK(principal)}</Text>
        </View>
        <View style={styles.kpi}>
          <Text style={styles.kpiLabel}>Rate</Text>
          <Text style={styles.kpiValue}>{formatRate(interestRatePct)}</Text>
        </View>
        <View style={styles.kpi}>
          <Text style={styles.kpiLabel}>Term</Text>
          <Text style={styles.kpiValue}>{termYears} yr</Text>
        </View>
      </View>

      <View style={styles.split}>
        <View style={styles.splitTrack}>
          <View
            style={[styles.splitInterest, { flex: Math.max(interestSharePct, 1) }]}
          />
          <View
            style={[styles.splitPrincipal, { flex: Math.max(principalSharePct, 1) }]}
          />
        </View>
        <View style={styles.splitLegend}>
          <Text style={styles.legendInterest}>
            Interest {formatCurrency(monthlyInterest)}
          </Text>
          <Text style={styles.legendPrincipal}>
            Principal {formatCurrency(monthlyPrincipal)}
          </Text>
        </View>
      </View>
    </GlassCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.md,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 56,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  hero: {
    gap: 4,
  },
  heroLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  heroValue: {
    ...typography.display,
    fontSize: 34,
    lineHeight: 40,
    color: colors.accent,
    fontVariant: ['tabular-nums'],
    letterSpacing: -0.4,
  },
  kpiRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  kpi: {
    flex: 1,
    gap: 4,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
  },
  kpiLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  kpiValue: {
    ...typography.headline,
    fontSize: 17,
    lineHeight: 22,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  split: {
    gap: spacing.sm,
  },
  splitTrack: {
    flexDirection: 'row',
    height: 8,
    borderRadius: radii.pill,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  splitInterest: {
    backgroundColor: colors.warning,
  },
  splitPrincipal: {
    backgroundColor: colors.accent,
  },
  splitLegend: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  legendInterest: {
    ...typography.caption,
    color: colors.warning,
    fontVariant: ['tabular-nums'],
    flexShrink: 1,
  },
  legendPrincipal: {
    ...typography.caption,
    color: colors.accent,
    fontVariant: ['tabular-nums'],
    textAlign: 'right',
    flexShrink: 1,
  },
  emptyCopy: {
    ...typography.caption,
    color: colors.textMuted,
    lineHeight: 20,
  },
});
